/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { withTranslation } from 'react-i18next';
import {
  Container,
  Grid,
  Header,
} from 'semantic-ui-react';
import ReactGA from 'react-ga4';
import config from '../../config';
import BookDashIcons from '../components/Svgs/BookDash';
import { fetchM } from '../actions/fetchM';
import { flashErrorMessage } from '../actions/flash';
import { TRANSLATION_FILES } from '../constants/translation';
import { isCountryVietnam } from '../constants/countries';
import { restrictedModuleAccess } from '../utils/moduleAccess';

class BookDash extends Component {
  static contextTypes = {
    store: PropTypes.object,
  }
  static propTypes = {
    appConfig: PropTypes.shape({
      data: PropTypes.shape({
        customerOwnedByDeveloperExecutive: PropTypes.bool,
        noeticIsEnabled: PropTypes.bool,
        ifcaIsEnabled: PropTypes.bool,
        insoftIsEnabled: PropTypes.bool,
        onlinePaymentEnabled: PropTypes.bool,
        leadManagementIsEnabled: PropTypes.bool,
        leadManagementWebURL: PropTypes.string,
        settings: PropTypes.shape({
          canManagePaymentSupervisor: PropTypes.bool,
          canManagePaymentSalesManager: PropTypes.bool,
          canManagePaymentSalesAdmin: PropTypes.bool,
          bookingHideLoanTracker: PropTypes.bool,
        }),
      }),
    }),
    auth: PropTypes.shape({
      isBranchLeader: PropTypes.bool,
      isBranchLeaderSupervisor: PropTypes.bool,
      isHeadquarters: PropTypes.bool,
      isDeveloper: PropTypes.bool,
      isDeveloperSupervisor: PropTypes.bool,
      isDeveloperSalesManager: PropTypes.bool,
      isDeveloperSalesAdmin: PropTypes.bool,
      isExternalAgent: PropTypes.bool,
      isExternalAgencyAdmin: PropTypes.bool,
      isSiteplan: PropTypes.bool,
      isSuperAdmin: PropTypes.bool,
      isAdminBasic: PropTypes.bool,
      isAdminCommunity: PropTypes.bool,
      creditCheckAccess: PropTypes.bool,
      companyBranch: PropTypes.shape({
        isExternal: PropTypes.bool,
      }),
      company: PropTypes.shape({
        countryCode: PropTypes.string,
      }),
    }),
    t: PropTypes.func,
  }
  static defaultProps = {
    appConfig: {},
    auth: {},
  }
  static mapStateToProps = state => ({
    appConfig: state.appConfig,
    auth: state.auth,
  })
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      companyApp: [],
    };
  }
  componentDidMount() {
    const { auth = {} } = this.props;
    if (auth.creditCheckAccess) {
      this.getCredit();
    }
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, res);
    });
  }
  async getCredit() {
    const { store } = this.context;
    try {
      await this.setLoading(true);
      const res = await store.dispatch(fetchM('/company/credit/', {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }));
      if (!res.ok) {
        const message = await res.text();
        await store.dispatch(flashErrorMessage({ message }));
      } else {
        const companyAppCredit = await res.json();
        if (companyAppCredit !== null) {
          await this.setState({ companyApp: companyAppCredit.companyApp });
        }
      }
      await this.setLoading(false);
    } catch (err) {
      await this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      store.dispatch(flashErrorMessage(err));
    }
  }
  isNotExternalAgencyAdmin() {
    const auth = this.props.auth;
    if (auth && auth.isExternalAgencyAdmin) {
      return false;
    }
    return true;
  }
  hasAccessDE() {
    const auth = this.props.auth;
    const appConfig = this.props.appConfig;
    if (auth &&
      auth.companyBranch &&
      !auth.companyBranch.isExternal &&
      appConfig &&
      appConfig.data &&
      appConfig.data.customerOwnedByDeveloperExecutive
    ) {
      return true;
    }
    return false;
  }
  hasNoetic() {
    const appConfig = this.props.appConfig;
    if (appConfig &&
      appConfig.data &&
      appConfig.data.noeticIsEnabled
    ) {
      return true;
    }
    return false;
  }
  hasIFCA() {
    const appConfig = this.props.appConfig;
    if (appConfig &&
      appConfig.data &&
      appConfig.data.ifcaIsEnabled
    ) {
      return true;
    }
    return false;
  }
  hasInsoft() {
    const appConfig = this.props.appConfig;
    if (appConfig &&
      appConfig.data &&
      appConfig.data.insoftIsEnabled
    ) {
      return true;
    }
    return false;
  }
  hasSalesforce() {
    const appConfig = this.props.appConfig;
    if (appConfig &&
      appConfig.data &&
      appConfig.data.externalServiceIsEnabled &&
      appConfig.data.externalServiceSFModeType === 2 // is IJM
    ) {
      return true;
    }
    return false;
  }
  modules() {
    let leadLink = config.get('env') === 'production' ? 'https://leads.mhub.my' : 'https://lead.stage.mhub.my';
    if (this.props.appConfig.data.leadManagementWebURL !== '') {
      leadLink = this.props.appConfig.data.leadManagementWebURL;
    }
    const isCreditAccess = this.props.auth.creditCheckAccess &&
      (this.state.companyApp && !this.state.companyApp.requiredHQApproval) &&
      !this.props.auth.isBranchLeader &&
      true;
    const { settings } = this.props.appConfig.data;
    const companyCountryCode = this.props.auth && this.props.auth.company && this.props.auth.company.countryCode;
    const restrictedAccess = restrictedModuleAccess(this.props.auth);
    return [
      {
        name: 'Projects',
        description: 'Project Details, Sales Chart, Reports',
        type: 'projects',
        route: '/projects/',
        icon: 'building',
        enabled: true,
        isBranchLeader: true,
        isDeveloper: true,
        isSiteplan: true,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      {
        name: 'Bookings',
        description: 'Reservations, Payment & Loan Status',
        type: 'bookings',
        route: isCountryVietnam(companyCountryCode) ? '/opportunities/' : '/bookings/',
        icon: 'book',
        enabled: true,
        isBranchLeader: true,
        isDeveloper: true,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
        hideForExternal: isCountryVietnam(companyCountryCode),
      },
      ...(isCountryVietnam(companyCountryCode) ? [{
        name: 'Sales',
        description: 'Reservations, Payment & Loan Status',
        type: 'bookings',
        route: '/bookings/',
        icon: 'book',
        enabled: true,
        isBranchLeader: true,
        isDeveloper: true,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      }] : []),
      {
        name: 'Users',
        description: 'User Access & Permissions',
        type: 'staff',
        route: '/users/',
        icon: 'users',
        enabled: true,
        isBranchLeader: this.props.auth.isBranchLeaderSupervisor || false,
        isDeveloper: false,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      {
        name: 'Lead Management',
        description: 'View and Manage Leads',
        type: 'lead',
        link: leadLink,
        icon: 'settings',
        enabled: true,
        isBranchLeader: this.props.appConfig.data.leadManagementIsEnabled,
        isDeveloper: this.props.appConfig.data.leadManagementIsEnabled,
        isHeadquarters: this.props.appConfig.data.leadManagementIsEnabled,
        isSuperAdmin: this.props.appConfig.data.leadManagementIsEnabled,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      this.hasNoetic() && {
        name: 'Post-Sale',
        description: 'Synchronize Bookings to Noetic',
        type: 'accounting',
        route: '/accounting/',
        icon: 'accounting',
        enabled: true,
        isBranchLeader: true,
        isDeveloper: false,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      this.hasIFCA() && {
        name: 'Post-Sale',
        description: 'Synchronize Bookings to IFCA',
        type: 'accounting',
        route: '/accounting_ifca/',
        icon: 'accounting',
        enabled: true,
        isBranchLeader: true,
        isDeveloper: false,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      this.hasInsoft() && {
        name: 'Post-Sale',
        description: 'Synchronize Bookings to Insoft',
        type: 'accounting',
        route: '/accounting_insoft/',
        icon: 'accounting',
        enabled: true,
        isBranchLeader: true,
        isDeveloper: false,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      {
        name: 'Payments',
        description: 'View and Manage Payments',
        type: 'payments',
        route: '/payments/',
        icon: 'settings',
        enabled: true,
        isBranchLeader: false,
        isDeveloper: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: settings && settings.canManagePaymentSupervisor,
        isDeveloperSalesManager: settings && settings.canManagePaymentSalesManager,
        isDeveloperSalesAdmin: settings && settings.canManagePaymentSalesAdmin,
        ...restrictedAccess,
      },
      {
        name: 'Customers',
        description: 'Contact information',
        type: 'customers',
        route: '/customers/',
        icon: 'child',
        enabled: true,
        isBranchLeader: true,
        isDeveloper: this.hasAccessDE() || false,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      {
        name: 'Bankers',
        description: 'View and Manage Panel Bankers and Preferred Bankers',
        type: 'preferred_bankers',
        route: '/preferred_bankers/',
        enabled: this.isNotExternalAgencyAdmin() && settings && !settings.bookingHideLoanTracker,
        isDeveloper: true,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
        ...restrictedAccess,
      },
      {
        name: 'Media',
        description: 'View and Manage Photos and Documents',
        type: 'pictures',
        route: '/media/',
        icon: 'picture',
        enabled: true,
        isBranchLeader: false,
        isDeveloper: false,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      {
        name: 'Credit Checks',
        description: 'Manage and Run Purchaser Credit Scores',
        type: 'creditcheck',
        route: '/customers/manage/credit_checks/',
        icon: 'dashboard',
        enabled: true,
        isBranchLeader: false,
        isDeveloper: isCreditAccess,
        isSiteplan: false,
        isHeadquarters: this.props.auth.creditCheckAccess && true,
        isSuperAdmin: this.props.auth.creditCheckAccess && true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      {
        name: 'Reports',
        description: 'Project Units, Bookings, Users Performance',
        type: 'reports',
        route: '/reports/',
        icon: 'chart bar',
        enabled: true,
        isBranchLeader: false,
        isDeveloper: false,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
        ...restrictedAccess,
      },
      {
        name: 'Activity Logs',
        description: 'Booking & Project Unit Activities',
        type: 'activities',
        route: '/activities/',
        icon: 'chart bar',
        enabled: true,
        isBranchLeader: false,
        isDeveloper: false,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      this.hasSalesforce() && {
        name: 'Salesforce Sync',
        description: 'View Salesforce Integration Status',
        type: 'settings',
        route: '/integrations/sf/',
        icon: 'settings',
        enabled: true,
        isBranchLeader: false,
        isDeveloper: false,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
      {
        name: 'Settings',
        description: 'Specification Presets, Currency Settings & Business Unit Setup',
        type: 'settings',
        route: '/settings/',
        icon: 'settings',
        enabled: true,
        isBranchLeader: false,
        isDeveloper: false,
        isSiteplan: false,
        isHeadquarters: true,
        isSuperAdmin: true,
        isDeveloperSupervisor: false,
        isDeveloperSalesManager: false,
        isDeveloperSalesAdmin: false,
      },
    ];
  }
  gaEvent = (e, module) => {
    ReactGA.event('BOOK_DASH', {
      category: 'BOOK_DASH',
      action: module.route || module.link,
      route: module.route || module.link,
      label: module.name,
      name: module.name,
    });
  };
  render() {
    const { t } = this.props;
    return (
      <Container>
        <Grid doubling columns={4} className="dash_grid">
          {
            this.modules()
              .filter((module) => {
                if (!module.enabled) return false;
                const companyBranch = this.props.auth.companyBranch;
                if (companyBranch && companyBranch.isExternal && module.hideForExternal) {
                  return false;
                }
                if (this.props.auth.isSuperAdmin && module.isSuperAdmin) {
                  return true;
                }
                if (this.props.auth.isAdminBasic && module.isAdminBasic) {
                  return true;
                }
                if (this.props.auth.isAdminCommunity && module.isAdminCommunity) {
                  return true;
                }
                if (this.props.auth.isHeadquarters && module.isHeadquarters) {
                  return true;
                }
                if (this.props.auth.isBranchLeader && module.isBranchLeader) {
                  return true;
                }
                if (this.props.auth.isDeveloper && module.isDeveloper) {
                  return true;
                }
                if (this.props.auth.isSiteplan && module.isSiteplan) {
                  return true;
                }
                if (this.props.auth.isDeveloperSupervisor && module.isDeveloperSupervisor) {
                  return true;
                }
                if (this.props.auth.isDeveloperSalesManager && module.isDeveloperSalesManager) {
                  return true;
                }
                if (this.props.auth.isDeveloperSalesAdmin && module.isDeveloperSalesAdmin) {
                  return true;
                }
                return false;
              })
              .map((module, idx) => (
                <Grid.Column key={idx}>
                  <Link
                    type="a"
                    href={module.link && module.link}
                    target={module.link && '_blank'}
                    to={module.link ? '' : module.route}
                    onClick={e => this.gaEvent(e, module)}
                  >
                    <Header textAlign="center" as="h3">
                      <p>
                        <BookDashIcons
                          type={module.type}
                          width="6em"
                          height="6em"
                          backfill="#F8F8F8"
                          iconfill="#00233B"
                        />
                      </p>
                      {t(module.name).toUpperCase()}
                      <Header.Subheader>
                        {t(module.description)}
                      </Header.Subheader>
                    </Header>
                  </Link>
                </Grid.Column>
            ))
          }
        </Grid>
      </Container>
    );
  }
}

BookDash.contextTypes = {
  store: PropTypes.object,
};

export default withTranslation(TRANSLATION_FILES.screens)(connect(BookDash.mapStateToProps)(BookDash));



// WEBPACK FOOTER //
// ./src/containers/BookDash.jsx